import type {
  BrandDetachRequest,
  BrandGroupRequest,
  BrandLabelRequest,
  KeywordDomainRequest,
  KeywordDomains,
  KeywordDomainsParams,
  KeywordGroup,
  KeywordGroupResetRequest,
  KeywordStatsRecompute,
  KeywordGroupResetResponse,
  KeywordGroupStatusRequest,
  KeywordGroupStatusResponse,
  ProjectKeywordsParams,
} from '@brand24-original/llm-types';
import { KeywordResponse, KeywordStatus } from '../../../models/monitor/KeywordResponse';
import { client } from '../../apiClient';
import { FirmData } from '../../../models/monitor/FirmData';
import { PROMPT_ENDPOINT } from '../prompt/PromptService';
import { KeywordsData } from '../../../models/tags/TagsResponse';

export const KEYWORDS_ENDPOINT = '/monitor/keywords';

export const getKeywordsData = async (id: string): Promise<KeywordResponse[]> => {
  return client(true, true)
    .get<KeywordResponse[]>(`${KEYWORDS_ENDPOINT}/list/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getCommonKeywordsWithTags = async (projectId: number): Promise<KeywordResponse[]> => {
  const { data } = await client(true, true).get<KeywordResponse[]>(
    `${KEYWORDS_ENDPOINT}/common-keywords-tags/${projectId}`
  );
  return data;
};

export const getCommonKeywords = async (projectId: number): Promise<KeywordsData[]> => {
  const { data } = await client(true, true).get<KeywordsData[]>(
    `${KEYWORDS_ENDPOINT}/common-keywords/${projectId}`
  );
  return data;
};

export const regenerateKeywords = async (id: number): Promise<KeywordResponse[]> => {
  return client(true, true)
    .get<KeywordResponse[]>(`${KEYWORDS_ENDPOINT}/regenerate-keywords/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getProjectKeywords = async (
  params: ProjectKeywordsParams
): Promise<KeywordGroup[]> => {
  const { data } = await client(true, true).get<KeywordGroup[]>(
    `${KEYWORDS_ENDPOINT}?projectId=${params.projectId}`
  );
  return data;
};

/**
 * Flips status for the whole canonical brand group (canonical + every alias) in a project.
 * Backs the grouped-row toggle on KeywordsPage. One request = one DB UPDATE.
 */
export const updateKeywordStatusByCanonical = async (
  body: KeywordGroupStatusRequest
): Promise<KeywordGroupStatusResponse> => {
  const { data } = await client(true, true).patch<KeywordGroupStatusResponse>(
    `${KEYWORDS_ENDPOINT}/by-canonical/status`,
    body
  );
  return data;
};

/**
 * „Przywróć domyślne” w wyborze marek na dashboardzie (konta z KEYWORDS_PAGE): backend zostawia 15 marek
 * z najwyższą widocznością (NEW_STATS) albo Share of Voice, z marką wybraną i główną, resztę wyłącza.
 */
export const resetKeywordGroupsToDefault = async (
  body: KeywordGroupResetRequest
): Promise<KeywordGroupResetResponse> => {
  const { data } = await client(true, true).patch<KeywordGroupResetResponse>(
    `${KEYWORDS_ENDPOINT}/by-canonical/reset-default`,
    body
  );
  return data;
};

/** Nazwy, których statystyki jeszcze się liczą po włączeniu — pusta lista = wszystko policzone. */
export const getKeywordStatsRecompute = async (projectId: number): Promise<KeywordStatsRecompute[]> => {
  const { data } = await client(true, true).get<KeywordStatsRecompute[]>(
    `${KEYWORDS_ENDPOINT}/recompute-status?projectId=${projectId}`
  );
  return data;
};

export const bulkUpdateKeywordStatus = async (
  keywordIds: number[],
  status: KeywordStatus
): Promise<{ updated: number; failed: number }> => {
  const { data } = await client(true, true).patch<{ updated: number; failed: number }>(
    `${KEYWORDS_ENDPOINT}/bulk-status`,
    { keywordIds, status }
  );
  return data;
};

export const updateKeywordStatus = async (
  keywordId: number,
  status: KeywordStatus
): Promise<{ success: boolean }> => {
  const { data } = await client(true, true).patch<{ success: boolean }>(
    `${KEYWORDS_ENDPOINT}/${keywordId}/status`,
    { status }
  );
  return data;
};

export const updateKeywords = async (id: string, firmData: FirmData): Promise<KeywordResponse> => {
  if (!firmData) {
    throw Error('Keyword is empty');
  }

  const keywordData = { keyword: firmData.type };
  return client(true, true)
    .post<KeywordResponse>(`${PROMPT_ENDPOINT}/update-keyword/${id}`, keywordData)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getKeywordDomains = async (params: KeywordDomainsParams): Promise<KeywordDomains[]> => {
  const { data } = await client(true, true).get<KeywordDomains[]>(
    `${KEYWORDS_ENDPOINT}/domains?projectId=${params.projectId}`
  );
  return data;
};

export const setKeywordDomain = async (request: KeywordDomainRequest): Promise<KeywordDomains> => {
  const { data } = await client(true, true).put<KeywordDomains>(
    `${KEYWORDS_ENDPOINT}/domains`,
    request
  );
  return data;
};

/** Gdzie nazwa należy w tym projekcie: osobno, pod inną marką albo jak mówi słownik (`null`). */
export const setBrandGroup = async (request: BrandGroupRequest): Promise<void> => {
  await client(true, true).patch(`${KEYWORDS_ENDPOINT}/split`, request);
};

/** Odłącza dołączoną markę w całości — wszystkie jej nazwy wracają do niej. */
export const detachBrand = async (request: BrandDetachRequest): Promise<void> => {
  await client(true, true).patch(`${KEYWORDS_ENDPOINT}/detach`, request);
};

/** Etykieta marki w tym projekcie; `label: null` przywraca nazwę domyślną. */
export const setBrandLabel = async (request: BrandLabelRequest): Promise<void> => {
  await client(true, true).patch(`${KEYWORDS_ENDPOINT}/brand-label`, request);
};
